import { Injectable } from '@angular/core';
import { Animal } from '../../animal';
import { AnimalEvent } from '../animal-event';
import { Dry } from './dry';
import {
    Filter,
    FilterOp,
    NetworkStatusService,
    SortMode,
    SortOrder
} from 'libs/arch/src/lib/repositories';
import {
    BaseIndexedDbRepository,
    SynchronizationService
} from 'libs/arch/src/lib/repositories/indexed-db';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { map, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ApiService } from '@homeleite/arch';

@Injectable({ providedIn: 'root' })
export class DriesRepository extends BaseIndexedDbRepository<Dry, Dry> {
    protected mapToEntity(dbItem: Dry): Dry {
        return Dry.fromJson(dbItem);
    }

    protected override readonly storeName: string = 'dries';

    protected override mapToSummary(item: Dry): Dry {
        return item;
    }

    protected override getSearchableFields(): string[] {
        return ['animal.name', 'animal.register', 'date'];
    }

    protected override getResourcePath(): string {
        return 'dries';
    }

    constructor(
        dbService: NgxIndexedDBService,
        synchronizationService: SynchronizationService,
        private apiService: ApiService,
        private networkStatus: NetworkStatusService
    ) {
        super(dbService, synchronizationService);

        this.registerAnimalReferences('animalId');
    }

    public override insert(dry: Dry): Observable<Dry> {
        return super.insert(dry).pipe(
            tap((savedDry) => {
                const transformedEvent: AnimalEvent = {
                    id: savedDry.id,
                    date: savedDry.date,
                    animal: savedDry.animal,
                    type: 'DRY',
                    created: savedDry.created
                };

                this.dbService.add('animalEvents', transformedEvent).subscribe({
                    error: (err) => {
                        console.error('Falha ao agregar evento de Secagem em animalEvents:', err);
                    }
                });
            })
        );
    }

    public findByAnimal(animal: Animal): Observable<Dry[]> {
        const animalId: string | undefined = animal?.id;
        if (!animalId) {
            return of([]);
        }

        const getFromDb = () => {
            return this.executeIndexedDbQuery({
                filters: [new Filter('animal.id', FilterOp.EQ, animalId)],
                sortOrders: [
                    new SortOrder('date', SortMode.DSC),
                    new SortOrder('created', SortMode.DSC)
                ]
            });
        };

        if (!this.networkStatus.isOnline()) {
            return getFromDb();
        }

        return this.fetchDriesBy(animalId)
            .pipe(catchError(() => getFromDb()));
    }

    private fetchDriesBy(animalId: string): Observable<Dry[]> {
        return this.apiService.root
            .path(this.getResourcePath())
            .path('animal')
            .path(animalId)
            .getJson<any[]>()
            .pipe(
                map((response) =>
                    Array.isArray(response) ? response.map((item) => Dry.fromJson(item)) : []
                ),
                tap((dries) => {
                    if (!dries?.length) return;

                    const integrationDries = dries.filter((dry) => {
                        const { auditData } = dry;
                        return (
                            auditData?.createdByIntegration || (auditData?.createdByApp && auditData?.modifiedByIntegration)
                        );
                    });

                    if (integrationDries.length > 0) {
                        this.saveLocally(integrationDries).subscribe({
                            error: (err) => console.error('Falha ao salvar secagens em lote:', err)
                        });
                    }
                })
            );
    }
}
